/**
 * Shared filter configuration for Tasks DataTable and Advanced Filters
 *
 * This is the single source of truth for:
 * 1. Defining available filters and their behaviors (array vs string, defaults)
 * 2. Mapping Column IDs (UI) -> URL Params -> API/Service Keys
 * 3. Deriving TypeScript interfaces for the application
 */

import {
  TaskCountsResponse,
  TaskFilterOption,
} from '@/services/api/tasksService';
import { PaginationParams } from '@/services/types/pagination-types';
import {
  CanonicalTaskStatus,
  TaskPriorityLabel,
} from '@/services/types/task-types';

// ----------------------------------------------------------------------------
// 1. Column IDs (UI Identifiers)
// ----------------------------------------------------------------------------

/**
 * Central repository of Column IDs.
 * Connects the UI (Columns) to the Logic (Filter Config).
 */
export const TableColumnIds = {
  PRIORITY: 'priority',
  DUE_DATE: 'due_date',
  MATTER_NAME: 'matter__name',
  CASE_STATUS: 'matter__status',
  TASK_TYPE: 'task_type',
  TASK_NAME: 'task_name',
  TASK_DESCRIPTION: 'task_description',
  TASK_STATUS: 'task_status',
  AGENT: 'agent',
  ASSIGNEE: 'assignee',
  LAST_UPDATE: 'latest_update_at',
  LAST_UPDATE_ACTOR: 'last_update_actor',
  LAST_UPDATE_TEXT: 'latest_update_text',
  CREATED_AT: 'created_at',
  RAINMAKER: 'rainmaker',
  INCIDENT: 'matter__matter_type',
  FIRM_NAME: 'matter__firm__name',
  COVERAGE: 'coverage',
  POLICE_DEPARTMENT: 'police_department',
  FACILITY: 'facility',
  FIRM_PRIMARY_STATE: 'firm_primary_state',
  CMS_STATUS: 'cms_status',
  SLA_RESOLUTION_DATE: 'sla_resolution_target',
  TASK_SOURCE: 'task_source',
} as const;

// ----------------------------------------------------------------------------
// 2. Filter Configuration (The Single Source of Truth)
// ----------------------------------------------------------------------------

export interface FilterDefinition {
  /**
   * The key expected by the useGetTasksQuery service hook.
   * Examples: 'statuses' (for status), 'matter__firm'
   */
  serviceKey: string;

  /**
   * The key used in the URL and Pagination State.
   * If omitted, defaults to `serviceKey`.
   * Example: 'status' (URL) vs 'statuses' (Service)
   */
  urlKey?: string;

  /**
   * The ID used by the DataTable column definition.
   * Used to map UI column actions to filter params.
   */
  columnId?: string;

  /**
   * Whether this parameter handles multiple values.
   * If true, the builder will use `ensureArray`.
   */
  isArray?: boolean;

  /** Default value to use if the param is undefined in state */
  defaultValue?: string | number | boolean | null | undefined;

  /** Whether this is a legacy filter that might be deprecated */
  isLegacy?: boolean;

  /**
   * Strategy for sorting filter options in the UI.
   * - 'preserve': Keep the order returned by the backend (semantic order)
   * - 'alphabetical': Sort alphabetically by label (for dynamic lists)
   * - 'explicit': Use explicitOrder array (for static options with specific UX order)
   * Default is 'preserve' if not specified.
   */
  sortOptions?: 'alphabetical' | 'preserve' | 'explicit';

  /**
   * Explicit ordering of option values by their `value` field.
   * Required when sortOptions is 'explicit'.
   * Options not in this array are appended at the end (alphabetically).
   */
  explicitOrder?: readonly string[];

  /**
   * Enable "Select all" for multiselect filters.
   * Set to false for high-cardinality filters (e.g., Matter) to prevent URL overflow.
   * Default: true (handled by DataTable component)
   */
  selectAllEnabled?: boolean;
}

export const FILTER_CONSTANTS = {} as const;

/**
 * Active task statuses for default filter presets.
 * Used by TaskListView for "Reset to Defaults" behavior.
 * These are tasks that require attention (not completed/cancelled).
 */
export const ACTIVE_STATUS_VALUES = [
  CanonicalTaskStatus.PENDING,
  CanonicalTaskStatus.IN_PROGRESS,
  CanonicalTaskStatus.BLOCKED,
] as const;

// ----------------------------------------------------------------------------
// Date Range Filter Configurations
// ----------------------------------------------------------------------------

/**
 * Configuration for date range filters.
 * Each entry defines the filter keys for a specific date range type.
 *
 * To add a new date range filter:
 * 1. Add entry here with startKey, endKey, and optional noDueDateKey
 * 2. Ensure backend supports the filter keys
 * 3. Use with useDateRangeFilter({ configKey: 'your_key' })
 */
export const DATE_RANGE_CONFIGS = {
  /**
   * Due date / Next action date filter.
   * Includes noDueDateKey for "tasks with no due date" option.
   */
  due_date: {
    startKey: 'due_date_after',
    endKey: 'due_date_before',
    noDueDateKey: 'no_due_date',
  },
  /**
   * Created at filter (when entity was created).
   * No "no date" option - all entities have a created_at.
   */
  created_at: {
    startKey: 'created_at_after',
    endKey: 'created_at_before',
  },
  /**
   * Last update filter (when entity was last modified).
   * No "no date" option - all entities have been updated at least once.
   */
  last_update: {
    startKey: 'last_update_after',
    endKey: 'last_update_before',
  },
  /**
   * SLA resolution target filter.
   * Includes noDueDateKey for "tasks with no SLA resolution target" option.
   */
  sla_resolution: {
    startKey: 'sla_resolution_target_after',
    endKey: 'sla_resolution_target_before',
    noDueDateKey: 'no_sla_resolution_target',
  },
} as const;

export type DateRangeConfigKey = keyof typeof DATE_RANGE_CONFIGS;

/**
 * Configuration Keys for Filter Definitions
 * Use these constants instead of string literals to refer to filters.
 */
export const FILTER_CONFIG_KEYS = {
  SEARCH: 'search',
  LIMIT: 'limit',
  OFFSET: 'offset',
  ORDERING: 'ordering',
  STATUS: 'status',
  PRIORITY: 'priority',
  TYPE: 'type',
  CANONICAL_TYPES: 'canonical_types',
  CANONICAL_TYPE_IDS: 'canonical_type_ids',
  MATTER_FIRM: 'matter__firm',
  FIRM_STATE: 'firm_primary_state',
  ASSIGNEE: 'assignee',
  MATTER: 'matter',
  MATTER_STATUS: 'matter_status',
  INCIDENT_TYPE: 'incident_type',
  COVERAGE_CARRIER: 'coverage_carrier',
  POLICE_DEPARTMENT: 'police_department',
  MEDICAL_PROVIDER: 'medical_provider',
  DUE_DATE_RANGE: 'due_date_range',
  DUE_DATE_BEFORE: 'due_date_before',
  DUE_DATE_AFTER: 'due_date_after',
  NO_DUE_DATE: 'no_due_date',
  LAST_UPDATE_RANGE: 'last_update_range',
  LAST_UPDATE_ACTOR: 'last_update_actor',
  CREATED_AT_RANGE: 'created_at_range',
  CANONICAL_GROUPS: 'canonical_groups',
  GROUP_TYPES: 'group_types',
  METADATA_TAG: 'metadata_tag',
  METADATA_TAG_EXCLUDE: 'metadata_tag_exclude',
  HAS_ACTIVE_RAINMAKER: 'has_active_rainmaker',
  EXTERNAL_CMS_UPDATE_STATUS: 'external_cms_update_status',
  SLA_RESOLUTION_OVERDUE: 'sla_resolution_overdue',
  SLA_RESOLUTION_TARGET_AFTER: 'sla_resolution_target_after',
  SLA_RESOLUTION_TARGET_BEFORE: 'sla_resolution_target_before',
  NO_SLA_RESOLUTION_TARGET: 'no_sla_resolution_target',
  GROUP: 'group',
  INCLUDE_ALL_CANONICAL: 'include_all_canonical',
  EXCLUDE_HIDDEN_MATTER_TASKS: 'exclude_hidden_matter_tasks',
  SOURCE: 'source',
  AGENT_STATUS: 'crashing_out_agent_status',
} as const;

export const TASK_FILTERS_CONFIG = {
  // --- Standard Search & Pagination ---
  [FILTER_CONFIG_KEYS.SEARCH]: {
    serviceKey: 'search',
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.LIMIT]: {
    serviceKey: 'limit',
    isArray: false,
    defaultValue: 50,
  },
  [FILTER_CONFIG_KEYS.OFFSET]: {
    serviceKey: 'offset',
    isArray: false,
    defaultValue: 0,
  },
  [FILTER_CONFIG_KEYS.ORDERING]: {
    serviceKey: 'ordering',
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.INCLUDE_ALL_CANONICAL]: {
    serviceKey: 'include_all_canonical',
    isArray: false,
    defaultValue: false,
  },

  // --- Core Task Properties ---
  [FILTER_CONFIG_KEYS.STATUS]: {
    serviceKey: 'statuses', // Service expects 'statuses'
    urlKey: 'status', // URL uses 'status'
    columnId: TableColumnIds.TASK_STATUS,
    isArray: true,
    sortOptions: 'preserve',
  },
  [FILTER_CONFIG_KEYS.PRIORITY]: {
    serviceKey: 'priority',
    columnId: TableColumnIds.PRIORITY,
    isArray: true,
    sortOptions: 'explicit',
    explicitOrder: [
      TaskPriorityLabel.IMMEDIATE,
      TaskPriorityLabel.URGENT,
      TaskPriorityLabel.NORMAL,
    ],
  },
  // The 'type' filter (Single UUID or similar)
  [FILTER_CONFIG_KEYS.TYPE]: {
    serviceKey: 'type',
    columnId: TableColumnIds.TASK_TYPE,
    isArray: false, // API doc says type is single value
  },
  // The 'canonical_types' filter (Multi-select names) - Legacy, kept for backwards compatibility
  [FILTER_CONFIG_KEYS.CANONICAL_TYPES]: {
    serviceKey: 'canonical_types',
    columnId: TableColumnIds.TASK_TYPE,
    isArray: true,
    sortOptions: 'alphabetical',
    isLegacy: true,
  },
  // The 'canonical_type_ids' filter (Multi-select by TaskType UUIDs)
  [FILTER_CONFIG_KEYS.CANONICAL_TYPE_IDS]: {
    serviceKey: 'canonical_type_ids',
    columnId: TableColumnIds.TASK_TYPE,
    isArray: true,
    sortOptions: 'alphabetical',
  },

  // --- Related Entities ---
  [FILTER_CONFIG_KEYS.MATTER_FIRM]: {
    serviceKey: 'matter__firm',
    columnId: TableColumnIds.FIRM_NAME,
    isArray: true,
    sortOptions: 'alphabetical', // Dynamic list
  },
  [FILTER_CONFIG_KEYS.FIRM_STATE]: {
    serviceKey: 'firm_primary_state',
    columnId: TableColumnIds.FIRM_PRIMARY_STATE,
    isArray: true,
    sortOptions: 'alphabetical', // Dynamic list of US states
  },
  [FILTER_CONFIG_KEYS.ASSIGNEE]: {
    serviceKey: 'assignee',
    columnId: TableColumnIds.ASSIGNEE,
    isArray: true,
    sortOptions: 'explicit', // Presets first (unassigned, managing paralegals), then alphabetical
    explicitOrder: [
      'unassigned',
      'managing_paralegals',
      'exclude_managing_paralegals',
    ],
    selectAllEnabled: false, // Prevent URL overflow with high-cardinality filter
  },
  [FILTER_CONFIG_KEYS.MATTER]: {
    serviceKey: 'matter',
    urlKey: 'matter', // explicitly explicit
    columnId: TableColumnIds.MATTER_NAME,
    isArray: true,
    sortOptions: 'alphabetical', // Dynamic list
    selectAllEnabled: false, // Prevent URL overflow with high-cardinality filter
  },
  [FILTER_CONFIG_KEYS.MATTER_STATUS]: {
    serviceKey: 'matter_status',
    columnId: TableColumnIds.CASE_STATUS,
    isArray: true,
    sortOptions: 'preserve', // Workflow stages often have semantic order
  },
  [FILTER_CONFIG_KEYS.INCIDENT_TYPE]: {
    serviceKey: 'incident_type',
    columnId: TableColumnIds.INCIDENT,
    isArray: true,
    sortOptions: 'alphabetical', // Dynamic list
  },
  [FILTER_CONFIG_KEYS.COVERAGE_CARRIER]: {
    serviceKey: 'coverage_carrier',
    columnId: TableColumnIds.COVERAGE,
    isArray: true,
    sortOptions: 'alphabetical', // Dynamic list
  },
  [FILTER_CONFIG_KEYS.POLICE_DEPARTMENT]: {
    serviceKey: 'police_department',
    columnId: TableColumnIds.POLICE_DEPARTMENT,
    isArray: true,
    sortOptions: 'alphabetical', // Dynamic list
  },
  [FILTER_CONFIG_KEYS.MEDICAL_PROVIDER]: {
    serviceKey: 'medical_provider',
    columnId: TableColumnIds.FACILITY,
    isArray: true,
    sortOptions: 'alphabetical', // Dynamic list
  },

  // --- Due Date Filters ---
  // Frontend computes date ranges for presets (Overdue, Due Today)
  // Backend uses simple DateFilter with date__ lookups
  [FILTER_CONFIG_KEYS.DUE_DATE_RANGE]: {
    serviceKey: 'due_date_range',
    columnId: TableColumnIds.DUE_DATE,
    isArray: true,
    sortOptions: 'preserve',
  },
  [FILTER_CONFIG_KEYS.DUE_DATE_BEFORE]: {
    serviceKey: 'due_date_before',
    columnId: TableColumnIds.DUE_DATE,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.DUE_DATE_AFTER]: {
    serviceKey: 'due_date_after',
    columnId: TableColumnIds.DUE_DATE,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.NO_DUE_DATE]: {
    serviceKey: 'no_due_date',
    columnId: TableColumnIds.DUE_DATE,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.LAST_UPDATE_RANGE]: {
    serviceKey: 'last_update_range',
    columnId: TableColumnIds.LAST_UPDATE,
    isArray: true,
    sortOptions: 'preserve', // Semantic order (Today, Yesterday...)
  },
  [FILTER_CONFIG_KEYS.LAST_UPDATE_ACTOR]: {
    serviceKey: 'last_update_actor',
    columnId: TableColumnIds.LAST_UPDATE_ACTOR,
    isArray: true,
    sortOptions: 'alphabetical', // User names and agent types sorted alphabetically
  },
  [FILTER_CONFIG_KEYS.CREATED_AT_RANGE]: {
    serviceKey: 'created_at_range',
    columnId: TableColumnIds.CREATED_AT,
    isArray: true,
    sortOptions: 'preserve', // Semantic order (Today, This Week, This Month)
  },

  // --- Categorization ---
  [FILTER_CONFIG_KEYS.CANONICAL_GROUPS]: {
    serviceKey: 'canonical_groups',
    isArray: true,
    sortOptions: 'alphabetical',
  },
  // Legacy support?
  [FILTER_CONFIG_KEYS.GROUP_TYPES]: {
    serviceKey: 'group_types',
    isArray: true,
    isLegacy: true,
  },

  // --- Metadata / Special ---
  [FILTER_CONFIG_KEYS.METADATA_TAG]: {
    serviceKey: 'metadata_tag',
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.METADATA_TAG_EXCLUDE]: {
    serviceKey: 'metadata_tag_exclude',
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.HAS_ACTIVE_RAINMAKER]: {
    serviceKey: 'has_active_rainmaker',
    columnId: TableColumnIds.RAINMAKER,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.EXTERNAL_CMS_UPDATE_STATUS]: {
    serviceKey: 'external_cms_update_status',
    columnId: TableColumnIds.CMS_STATUS,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.SLA_RESOLUTION_OVERDUE]: {
    serviceKey: 'sla_resolution_overdue',
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.SLA_RESOLUTION_TARGET_AFTER]: {
    serviceKey: 'sla_resolution_target_after',
    columnId: TableColumnIds.SLA_RESOLUTION_DATE,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.SLA_RESOLUTION_TARGET_BEFORE]: {
    serviceKey: 'sla_resolution_target_before',
    columnId: TableColumnIds.SLA_RESOLUTION_DATE,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.NO_SLA_RESOLUTION_TARGET]: {
    serviceKey: 'no_sla_resolution_target',
    columnId: TableColumnIds.SLA_RESOLUTION_DATE,
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.GROUP]: {
    serviceKey: 'group',
    isArray: false,
  },
  [FILTER_CONFIG_KEYS.EXCLUDE_HIDDEN_MATTER_TASKS]: {
    serviceKey: 'exclude_hidden_matter_tasks',
    isArray: false,
    defaultValue: false,
  },

  // --- Source Filter ---
  [FILTER_CONFIG_KEYS.SOURCE]: {
    serviceKey: 'source',
    columnId: TableColumnIds.TASK_SOURCE,
    isArray: true,
    sortOptions: 'preserve', // Keep backend order (static list)
  },

  // --- Agent Status Filter ---
  [FILTER_CONFIG_KEYS.AGENT_STATUS]: {
    serviceKey: 'crashing_out_agent_status',
    columnId: TableColumnIds.AGENT,
    isArray: true,
    sortOptions: 'preserve', // Semantic order: needs_review, working, not_running
  },
} as const;

// ----------------------------------------------------------------------------
// 3. Derived Mappings (Backwards Compatibility)
// ----------------------------------------------------------------------------

/**
 * Helper type to resolve the URL key (defaulting to serviceKey if not set)
 */
type GetUrlKey<K extends keyof typeof TASK_FILTERS_CONFIG> =
  (typeof TASK_FILTERS_CONFIG)[K] extends { urlKey: infer U }
    ? U extends string
      ? U
      : (typeof TASK_FILTERS_CONFIG)[K]['serviceKey']
    : (typeof TASK_FILTERS_CONFIG)[K]['serviceKey'];

/**
 * Helper to resolve the URL key (defaulting to serviceKey if not set)
 * Now generic to preserve literal types.
 *
 * This is the scalable solution: new filters added to the config will automatically
 * propagate their literal types through the system, and TypeScript will correctly
 * enforce whether they are arrays or booleans at the call site. No manual maintenance required.
 */
export const getFilterUrlKey = <K extends keyof typeof TASK_FILTERS_CONFIG>(
  key: K,
): GetUrlKey<K> => {
  const config = TASK_FILTERS_CONFIG[key] as FilterDefinition;
  return (config.urlKey || config.serviceKey) as GetUrlKey<K>;
};

/**
 * Maps column IDs to API filter parameter keys (URL keys)
 * Used when a filter change in the UI needs to update the API query
 */
export const COLUMN_TO_API_FILTER_MAP = Object.entries(
  TASK_FILTERS_CONFIG,
).reduce(
  (acc, [, config]) => {
    const def = config as FilterDefinition;
    if (def.columnId) {
      const urlKey = def.urlKey || def.serviceKey;
      acc[def.columnId] = urlKey;

      // Handle edge case where multiple columns might map to same filter,
      // or we want to support the direct key as well.
      // For now, we stick to the explicit columnId mapping.
    }
    return acc;
  },
  {} as Record<string, string>,
);

// Add manual overrides for things that don't fit the strict 1:1 config model perfectly
// or were present in the old map but modeled slightly differently.
Object.assign(COLUMN_TO_API_FILTER_MAP, {
  status: 'status', // Map 'status' col to 'status' param
});

/**
 * Maps API filter keys back to column IDs
 * Used when API filter state needs to be displayed in the UI
 */
export const API_TO_COLUMN_FILTER_MAP = Object.entries(
  TASK_FILTERS_CONFIG,
).reduce(
  (acc, [, config]) => {
    const def = config as FilterDefinition;
    if (def.columnId) {
      const urlKey = def.urlKey || def.serviceKey;
      acc[urlKey] = def.columnId;
    }
    return acc;
  },
  {} as Record<string, string>,
);
// Manual overrides for reverse mapping
Object.assign(API_TO_COLUMN_FILTER_MAP, {
  task_name: 'type', // Legacy map
});

/**
 * API filter keys used by Advanced Filters panel
 * These should match the backend API parameter names (URL keys)
 */
export const ADVANCED_FILTER_KEYS = {
  TASK_TYPES: getFilterUrlKey(FILTER_CONFIG_KEYS.CANONICAL_TYPE_IDS),
  TASK_GROUPS: getFilterUrlKey(FILTER_CONFIG_KEYS.CANONICAL_GROUPS),
  STATUS: getFilterUrlKey(FILTER_CONFIG_KEYS.STATUS),
  PRIORITY: getFilterUrlKey(FILTER_CONFIG_KEYS.PRIORITY),
  FIRM: getFilterUrlKey(FILTER_CONFIG_KEYS.MATTER_FIRM),
  ASSIGNEE: getFilterUrlKey(FILTER_CONFIG_KEYS.ASSIGNEE),
  CASE_STATUS: getFilterUrlKey(FILTER_CONFIG_KEYS.MATTER_STATUS),
  DUE_DATE_BEFORE: getFilterUrlKey(FILTER_CONFIG_KEYS.DUE_DATE_BEFORE),
  DUE_DATE_AFTER: getFilterUrlKey(FILTER_CONFIG_KEYS.DUE_DATE_AFTER),
  NO_DUE_DATE: getFilterUrlKey(FILTER_CONFIG_KEYS.NO_DUE_DATE),
  LAST_UPDATE_RANGE: getFilterUrlKey(FILTER_CONFIG_KEYS.LAST_UPDATE_RANGE),
  RAINMAKER: getFilterUrlKey(FILTER_CONFIG_KEYS.HAS_ACTIVE_RAINMAKER),
  COVERAGE_CARRIER: getFilterUrlKey(FILTER_CONFIG_KEYS.COVERAGE_CARRIER),
  POLICE_DEPARTMENT: getFilterUrlKey(FILTER_CONFIG_KEYS.POLICE_DEPARTMENT),
} as const;

/**
 * Filters that should be reset when switching verticals.
 * By default, we preserve context (search, status, etc.), but we MUST reset:
 * 1. Pagination (offset) - to start at page 1
 * 2. Vertical param - handled explicitly by the selector
 */
export const VERTICAL_SWITCH_RESET_KEYS = [
  getFilterUrlKey(FILTER_CONFIG_KEYS.OFFSET),
] as const;

// ----------------------------------------------------------------------------
// 4. Derived Types
// ----------------------------------------------------------------------------

/**
 * Metadata for advanced filters panel
 * Passed from Vertical Pages to TaskListView
 */
export interface AdvancedFiltersMetadata {
  groupCounts?: TaskCountsResponse;
  firmOptions?: TaskFilterOption[];
  assigneeOptions?: TaskFilterOption[];
  caseStatusOptions?: TaskFilterOption[];
  priorityOptions?: TaskFilterOption[];
  lastUpdateOptions?: TaskFilterOption[];
  lastUpdateActorOptions?: TaskFilterOption[];
  statusOptions?: TaskFilterOption[];
  coverageOptions?: TaskFilterOption[];
  policeDepartmentOptions?: TaskFilterOption[];
  medicalProviderOptions?: TaskFilterOption[];
}

/**
 * Task filter parameters from URL/Pagination State
 * This represents the "Frontend" view of filters.
 *
 * This type is now AUTOMATICALLY DERIVED from config/filterConfig.ts.
 * No manual synchronization required.
 */

// Helper type to extract keys that map to array types
type ArrayFilterKeys = {
  [K in keyof typeof TASK_FILTERS_CONFIG]: (typeof TASK_FILTERS_CONFIG)[K]['isArray'] extends true
    ? K
    : never;
}[keyof typeof TASK_FILTERS_CONFIG];

// Helper type to extract keys that map to string/single types
type SingleFilterKeys = {
  [K in keyof typeof TASK_FILTERS_CONFIG]: (typeof TASK_FILTERS_CONFIG)[K]['isArray'] extends true
    ? never
    : K;
}[keyof typeof TASK_FILTERS_CONFIG];

// Map the config keys to their URL parameter names
// We use the config keys as the internal interface keys for consistency
export type TaskFilterParams = PaginationParams & {
  // Array filters can often accept a single string in URL, which we parse to array
  [K in ArrayFilterKeys]?: string | string[];
} & {
  // Single value filters
  [K in SingleFilterKeys]?: string | number | boolean; // Allow boolean for flags
};

/**
 * Task service parameters expected by the API
 * This represents the "Backend" view of filters (serialized keys).
 *
 * Uses Key Remapping to map config keys (e.g. 'status') to service keys (e.g. 'statuses').
 */
export type TaskServiceFilterParams = {
  [K in keyof typeof TASK_FILTERS_CONFIG as (typeof TASK_FILTERS_CONFIG)[K]['serviceKey']]?: (typeof TASK_FILTERS_CONFIG)[K]['isArray'] extends true
    ? string | string[]
    : string | number | boolean;
};

/**
 * Advanced filter state (TaskFilterParams without pagination)
 */
export type AdvancedFilterState = Omit<
  TaskFilterParams,
  keyof PaginationParams
>;
